import type { RequestFn } from '../http';
import type { CWCompany, CWReference, CWTicket } from '../types';

export interface CWSiteWithAddress {
  id: number;
  name: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface CWContactInput {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
}

export interface CWContactDetail {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
}

export function createCwLookupsApi(request: RequestFn) {
  return {
    async getOpenTickets(options?: {
      search?: string;
      pageSize?: number;
      page?: number;
      boardIds?: number[];
      includeClosed?: boolean;
      companyId?: number;
      onlyMine?: boolean;
    }) {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.pageSize) params.set('pageSize', String(options.pageSize));
      if (options?.page) params.set('page', String(options.page));
      if (options?.boardIds && options.boardIds.length > 0) {
        params.set('boardIds', options.boardIds.join(','));
      }
      if (options?.includeClosed) params.set('includeClosed', 'true');
      if (options?.companyId) params.set('companyId', String(options.companyId));
      if (options?.onlyMine) params.set('onlyMine', 'true');
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<{ tickets: CWTicket[]; totalCount: number }>(`/cw/tickets${query}`);
    },

    async getTicketDetails(ticketId: number) {
      return request<{ ticket: CWTicket }>(`/cw/tickets/${ticketId}`);
    },

    async searchCompanies(query: string, options?: { page?: number; pageSize?: number }) {
      const params = new URLSearchParams();
      params.set('q', query);
      if (options?.page != null) params.set('page', String(options.page));
      if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
      return request<{ companies: CWCompany[]; hasMore: boolean }>(
        `/cw/companies/search?${params.toString()}`
      );
    },

    async getCompanySites(companyId: number) {
      return request<{ sites: CWSiteWithAddress[] }>(`/cw/companies/${companyId}/sites`);
    },

    async getCompanyContacts(companyId: number) {
      return request<{ contacts: CWReference[] }>(`/cw/companies/${companyId}/contacts`);
    },

    async createCwContact(companyId: number, input: CWContactInput) {
      return request<{ contact: CWContactDetail }>(
        `/cw/companies/${companyId}/contacts`,
        { method: 'POST', body: JSON.stringify(input) }
      );
    },

    async updateCwContact(contactId: number, input: Partial<CWContactInput>) {
      return request<{ contact: CWContactDetail }>(
        `/cw/contacts/${contactId}`,
        { method: 'PUT', body: JSON.stringify(input) }
      );
    },

    async getBoards() {
      return request<{ boards: CWReference[] }>('/cw/boards');
    },

    async getBoardTypes(boardId: number) {
      return request<{ types: CWReference[] }>(`/cw/boards/${boardId}/types`);
    },

    async getBoardStatuses(boardId: number) {
      return request<{ statuses: CWReference[] }>(`/cw/boards/${boardId}/statuses`);
    },

    async getBoardSubtypes(boardId: number, typeId: number) {
      return request<{ subtypes: CWReference[] }>(
        `/cw/boards/${boardId}/types/${typeId}/subtypes`
      );
    },

    async getBoardItems(boardId: number, typeId?: number, subtypeId?: number) {
      const params = new URLSearchParams();
      if (typeId) params.set('typeId', String(typeId));
      if (subtypeId) params.set('subtypeId', String(subtypeId));
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<{ items: CWReference[] }>(`/cw/boards/${boardId}/items${query}`);
    },

    async getPriorities() {
      return request<{ priorities: CWReference[] }>('/cw/priorities');
    },

    async getMembers() {
      return request<{ members: { id: number; identifier: string; name: string }[] }>(
        '/cw/members'
      );
    },
  };
}
